import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import MIHeader from '../MIHeader';
import { fmtNum, fmtMonth, slugify, getLatestPeriod, fetchLatestFortnightBreakdown, formatFortnightRange } from '../utils';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../../../hooks/useTranslation';
import { localizedFactoryName, localizedStateName, localizedDistrictName } from '../../../utils/localizedDisplayName';

const FactoriesBrowsePage = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stateFilter = searchParams.get('state') || '';
  const districtFilter = searchParams.get('district') || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(null);
  const [rows, setRows] = useState([]);
  const [fortnight, setFortnight] = useState(null);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(30);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const latest = await getLatestPeriod();
        const [{ data, error: fErr }, fortnightBreakdown] = await Promise.all([
          supabase
            .from('mi_v_release_orders_canonical')
            .select('mill_id, canonical_mill_name, canonical_state, canonical_district, allocated_qty_mt')
            .eq('period_month', latest)
            .not('canonical_mill_name', 'eq', '')
            .order('allocated_qty_mt', { ascending: false }),
          fetchLatestFortnightBreakdown(),
        ]);
        if (fErr) throw fErr;
        if (!isMounted) return;
        setMonth(latest);
        setFortnight(fortnightBreakdown);
        const withFortnight = (data || []).map((r) => ({
          ...r,
          fortnightMt: fortnightBreakdown ? (fortnightBreakdown.byMill.get(r.mill_id) ?? 0) : null,
        }));
        if (fortnightBreakdown) withFortnight.sort((a, b) => b.fortnightMt - a.fortnightMt);
        setRows(withFortnight);
      } catch (e) {
        if (isMounted) setError(e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const allStates = [...new Set(rows.map((r) => r.canonical_state))].filter(Boolean).sort();
  const selectedStateCanonical = allStates.find((s) => slugify(s) === stateFilter);
  const districtsForState = stateFilter
    ? [...new Set(rows.filter((r) => slugify(r.canonical_state) === stateFilter).map((r) => r.canonical_district))].filter(Boolean).sort()
    : [];

  let filtered = rows;
  if (stateFilter) filtered = filtered.filter((r) => slugify(r.canonical_state) === stateFilter);
  if (districtFilter) filtered = filtered.filter((r) => slugify(r.canonical_district) === districtFilter);
  if (search.trim().length >= 2) filtered = filtered.filter((r) => r.canonical_mill_name.toLowerCase().includes(search.toLowerCase()));

  const handleStateChange = (e) => {
    const val = e.target.value;
    const params = {};
    if (val) params.state = val;
    setSearchParams(params);
    setVisibleCount(30);
  };
  const handleDistrictChange = (e) => {
    const val = e.target.value;
    const params = {};
    if (stateFilter) params.state = stateFilter;
    if (val) params.district = val;
    setSearchParams(params);
    setVisibleCount(30);
  };

  const shown = filtered.slice(0, visibleCount);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Helmet>
        <title>All Factories — Sugar Mill Quota Data | SugarHathi Market Intelligence</title>
        <meta name="description" content="Mill-wise sugar quota release-order data across 621+ factories in India. Live monthly allocation figures, verified by SugarHathi." />
      </Helmet>
      <MIHeader subtitle={t('marketIntelligence.allFactories', 'All factories')} />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl py-8">
        <h1 className="text-2xl font-black text-[var(--text-primary)] mb-1">{t('marketIntelligence.allFactoriesCount', 'All factories ({{count}})', { count: filtered.length })}</h1>
        <p className="text-sm text-[var(--text-muted)] mb-4">
          {fortnight
            ? `${formatFortnightRange(fortnight.periodStart, fortnight.periodEnd, language) || fortnight.periodLabel} allocation`
            : `${t('marketIntelligence.completeMonthNote', 'Last complete month')} — ${fmtMonth(month)}`}
        </p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs text-[var(--text-muted)] block mb-1.5">{t('marketplace.state', 'State')}</label>
            <select
              value={stateFilter}
              onChange={handleStateChange}
              className="w-full bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] rounded-[var(--radius-input)] px-3 py-2.5 text-sm font-medium"
            >
              <option value="" style={{ background: 'var(--bg-primary)' }}>{t('marketplace.allStates', 'All States')}</option>
              {allStates.map((s) => (
                <option key={s} value={slugify(s)} style={{ background: 'var(--bg-primary)' }}>{localizedStateName(s, language)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--text-muted)] block mb-1.5">{t('mandi.selectDistrict', 'District')}</label>
            <select
              value={districtFilter}
              onChange={handleDistrictChange}
              disabled={!stateFilter}
              className="w-full bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] rounded-[var(--radius-input)] px-3 py-2.5 text-sm font-medium disabled:opacity-40"
            >
              <option value="" style={{ background: 'var(--bg-primary)' }}>{t('marketIntelligence.allDistricts', 'All districts')}</option>
              {districtsForState.map((d) => (
                <option key={d} value={slugify(d)} style={{ background: 'var(--bg-primary)' }}>{localizedDistrictName(d, selectedStateCanonical, language)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-[var(--bg-card)] border border-[var(--border-primary)] rounded-[var(--radius-input)] px-3 py-2.5 mb-6">
          <Icon name="Search" size={16} className="text-[var(--text-muted)]" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisibleCount(30); }}
            placeholder={t('marketIntelligence.searchFactoryPlaceholder', 'Search factory name within results')}
            className="flex-1 bg-transparent text-[var(--text-primary)] text-sm outline-none placeholder:text-[var(--text-muted)]"
          />
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-[var(--text-muted)]">
            <Icon name="Loader" size={32} className="animate-spin mb-3" />
            <p>{t('common.loading', 'Loading...')}</p>
          </div>
        )}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-[var(--text-muted)]">
            <Icon name="AlertTriangle" size={32} className="mb-3 text-[var(--error)]" />
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="space-y-2">
              {shown.map((f, i) => (
                <button
                  key={i}
                  onClick={() => navigate(`/market-intelligence/factory/${slugify(f.canonical_mill_name)}`)}
                  className="w-full bg-[var(--bg-card)] border border-[var(--border-light)] rounded-[var(--radius-input)] p-3.5 flex items-center justify-between text-left hover:bg-[var(--bg-surface)] transition-colors"
                >
                  <div className="min-w-0 pr-2">
                    <p className="text-sm text-[var(--text-primary)] truncate">{localizedFactoryName(f, language, 'canonical_mill_name')}</p>
                    <p className="text-xs text-[var(--text-muted)]">
                      <span
                        role="link"
                        onClick={(e) => { e.stopPropagation(); navigate(`/market-intelligence/district/${slugify(f.canonical_district)}`); }}
                        className="hover:underline"
                      >{localizedDistrictName(f.canonical_district, f.canonical_state, language)}</span>, {localizedStateName(f.canonical_state, language)}
                    </p>
                  </div>
                  <p className="text-sm font-bold text-[var(--text-primary)] whitespace-nowrap">{fmtNum(fortnight ? f.fortnightMt : f.allocated_qty_mt)} MT</p>
                </button>
              ))}
              {filtered.length === 0 && <p className="text-sm text-[var(--text-muted)]">{t('marketIntelligence.noFactoriesMatchThisFilter', 'No factories match this filter.')}</p>}
            </div>
            {visibleCount < filtered.length && (
              <button
                onClick={() => setVisibleCount((v) => v + 30)}
                className="w-full mt-3 border text-sm font-medium rounded-[var(--radius-button)] py-2.5"
                style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
              >
                {t('marketIntelligence.loadMoreRemaining', 'Load more ({{count}} remaining)', { count: filtered.length - visibleCount })}
              </button>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default FactoriesBrowsePage;

